import React, { useCallback, useEffect, useState } from 'react';
import CardShell from '../../components/CardShell';
import { Cell } from '../../components/InlineCells';
import { useP } from '../../i18n/useP';
import { getOpeningItems } from '../../api/financials_bs';
import { putForecastAssumption } from '../../api/forecastAssumptions';
import '../treasury/tabs/StartingStepper.css';
import './StartingPointPage.css';

/* PLAN DE LIQUIDITÉ › POINT DE DÉPART (2026-09-28) : le premier détail de
   « Modifier les calculs ». Il ne porte plus que la TRÉSORERIE de départ. Sa
   source se choisit sur sa ligne du plan (colonne Source, Finance ou
   Manuel) : Finance, elle se lit dans la comptabilité ; manuel, elle se tape
   ici ou dans la colonne « Solde initial ». (Les postes
   ouverts ont tenu une étape 2 ici une journée : un solde saisi à deux
   endroits, dont un loin de l'écran qui l'écoule.) Hors exercice de départ, le
   plan part de décembre du précédent, qui se lit sans se taper. */

const MOIS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

/* Une étape numérotée, repliable : le costume du point de départ de la
   Simulation 12M (`StartingStepper.css`). Déclarée HORS de la page : une
   étape définie dans le rendu serait remontée à chaque rendu, saisie comprise. */
function Step({ n, open, onToggle, title, sub, children }) {
  return (
    <section className={`stp-step${open ? ' is-open' : ''}`}>
      <button type="button" className="stp-step__head" aria-expanded={!!open} onClick={() => onToggle(n)}>
        <span className="stp-step__num">{n}</span>
        <span className="stp-step__titles">
          <span className="stp-step__title">{title}</span>
          {sub && <span className="stp-step__sub">{sub}</span>}
        </span>
        <span className="stp-step__chev" aria-hidden>{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className="stp-step__body">{children}</div>}
    </section>
  );
}

export default function StartingPointPage({ activeTenant, year, unit = 'kchf', closeAction = null, onChanged }) {
  const p = useP();
  const tid = activeTenant?.id;
  const [v, setV] = useState(null);
  const [open, setOpen] = useState({ 1: true });

  const load = useCallback(async () => {
    if (!tid || !year) return;
    const r = await getOpeningItems(tid, year);
    setV(r && !r.error ? r.data : null);
  }, [tid, year]);
  useEffect(() => { load(); }, [load]);

  if (!tid) return null;
  if (!v) return <p className="spt-empty">{p('Chargement…')}</p>;

  const div = unit === 'chf' ? 1 : 1000;
  const uLbl = unit === 'chf' ? 'CHF' : 'kCHF';
  const fmt = (x) => (x == null ? '–' : Math.round(Number(x) / div).toLocaleString('de-CH'));
  const money = (chf) => ({
    value: chf == null ? null : Math.round(Number(chf) / div), type: 'number',
    format: (x) => (x == null ? '' : Number(x).toLocaleString('de-CH')),
    toDraft: (x) => (x == null ? '' : String(x)),
  });
  const put = async (key, value) => {
    await putForecastAssumption(tid, year, key, value);
    await load();
    if (onChanged) onChanged();
  };
  const cc = Number(v.closed_count) || 0;
  const closLbl = cc >= 1 ? `${p('à fin')} ${MOIS[cc - 1].toLowerCase()} ${year}` : `${p('au')} 31.12.${year - 1}`;

  const typedCash = v.cash?.typed != null ? Number(v.cash.typed) : null;
  const finCash = v.cash?.finance != null ? Number(v.cash.finance) : null;
  const cashSrc = v.sources?.cash;
  const cash = !v.start ? (v.plan_opening != null ? Number(v.plan_opening) : null)
    : cashSrc === 'manual' ? typedCash : finCash;

  return (
    <CardShell title={p('Point de départ')} subtitle={`${closLbl} · ${uLbl}`}
               primaryAction={closeAction} flush>
      <div className="spt">
        <div className="stp-wrap">
          <Step n={1} open={open[1]} onToggle={(n) => setOpen((o) => ({ ...o, [n]: !o[n] }))}
                title={p('Trésorerie de départ')} sub={`${fmt(cash)} ${uLbl}`}>
            <div className="spt-row">
              <span className="spt-lbl">{p('Solde de départ')}</span>
              {cashSrc === 'manual' ? (
                <span className={`spt-val${cash == null ? ' lk-todo' : ''}`}>
                  <Cell {...money(cash)} canWrite placeholder={p('à saisir')}
                        onSave={(x) => put('cash_start', x == null ? null : { amount: x * div })} />
                </span>
              ) : (
                <span className="spt-val spt-val--ro">{fmt(cash)}</span>
              )}
              {cashSrc === 'finance' && <span className="spt-ref">{p('lu de Finance')}</span>}
              {!v.start && <span className="spt-ref">{p('décembre')} {year - 1}</span>}
            </div>
          </Step>
        </div>
      </div>
    </CardShell>
  );
}
